import { useTranslation } from 'react-i18next';

import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { roundToDecimals } from '@/shared/lib/math';

import {
    ColorValue,
    DirectionTypeValue,
    FlagValue,
    PercentageValue,
} from '../value-formatter/value-formatter';

import AlgotechLogo from '@/shared/assets/icons/algotech-logo.svg';

import cls from './column-formatter.module.css';

dayjs.extend(utc);
dayjs.extend(timezone);

export function createColorColumnFormatter<T>(
    colorSelector: (record: T) => string | undefined,
): (value: unknown, record: T) => JSX.Element {
    function ColorValueComponent(_: unknown, record: T): JSX.Element {
        const color = colorSelector(record) || '#FFFFFF';

        return <ColorValue color={color} />;
    }

    ColorValueComponent.displayName = 'ColorValueComponent';

    return ColorValueComponent;
}

export function createFlagColumnFormatter<T>(
    flagSelector: (record: T) => boolean,
    onFlagChange: (id: number, checked: boolean) => void,
    idSelector: (record: T) => number,
    colorSelector?: (record: T) => string | undefined,
): (value: unknown, record: T) => JSX.Element {
    function FlagValueComponent(_: unknown, record: T): JSX.Element {
        const isChecked = flagSelector(record);
        const id = idSelector(record);
        const color = colorSelector ? colorSelector(record) : undefined;

        return (
            <FlagValue
                {...(color ? { color } : {})}
                id={id}
                isChecked={isChecked}
                onFlagChange={onFlagChange}
            />
        );
    }

    FlagValueComponent.displayName = 'FlagValueComponent';

    return FlagValueComponent;
}

export function createPercentageBadgeFormatter<T>(
    valueSelector: (record: T) => number | undefined,
    options: {
        precision?: number;
        shouldShowDirection?: boolean;
        isAbsoluteValue?: boolean;
        shouldShowSign?: boolean;
        shouldUseColorsBasedOnSign?: boolean;
    } = {},
): (value: unknown, record: T) => JSX.Element {
    function PercentageValueComponent(_: unknown, record: T): JSX.Element {
        const selectedValue = valueSelector(record);
        const finalValue = selectedValue !== undefined ? selectedValue : 0;

        return (
            <PercentageValue
                options={options}
                value={finalValue}
            />
        );
    }

    PercentageValueComponent.displayName = 'PercentageValueComponent';

    return PercentageValueComponent;
}

export function createDirectionTypeFormatter<T>(
    directionTypeSelector: (record: T) => number,
): (value: unknown, record: T) => JSX.Element {
    function DirectionTypeValueComponent(_: unknown, record: T): JSX.Element {
        const isRecovered = directionTypeSelector(record);

        return <DirectionTypeValue isRecovered={!!isRecovered} />;
    }

    DirectionTypeValueComponent.displayName = 'DirectionTypeValueComponent';

    return DirectionTypeValueComponent;
}

export const createRoundedNumericColumns = <TRecord,>(
    fieldNames: Array<keyof TRecord>,
) =>
    fieldNames.reduce(
        (acc, fieldName) => {
            acc[fieldName as string] = {
                key: fieldName as string,
                render: (value) => {
                    if (Number.isNaN(value)) {
                        return String(value);
                    }

                    return roundToDecimals(value as number, 3);
                },
            };

            return acc;
        },
        {} as Record<
            string,
            {
                key: string;
                render: (value: number | string) => number | string;
            }
        >,
    );

export function createDateFormatter<T>(
    dateSelector: (record: T) => number,
    options: {
        shouldShowUTC?: boolean;
        timezone?: string;
    } = {},
): (value: unknown, record: T) => JSX.Element {
    function DateValueComponent(_: unknown, record: T): JSX.Element {
        const timestamp = dateSelector(record);
        const { shouldShowUTC = false, timezone: tz } = options;

        // Convert Unix timestamp to dayjs object
        const date = dayjs.unix(timestamp);

        // If timezone is provided, convert to that timezone
        const zonedDate = tz ? date.tz(tz) : date.utc();

        // Format date and time
        const formattedDate = zonedDate.format('DD/MM/YYYY HH:mm:ss');

        // Get UTC offset for display
        let utcLabel = '';

        if (shouldShowUTC) {
            if (!tz || tz === 'UTC' || tz === 'Etc/UTC') {
                utcLabel = ' (UTC)';
            } else {
                // Get the offset in minutes and convert to hours
                const offsetMinutes = zonedDate.utcOffset();
                const offsetHours = offsetMinutes / 60;
                const absOffsetHours = Math.abs(offsetHours);
                const hours = Math.floor(absOffsetHours);
                const minutes = Math.round((absOffsetHours - hours) * 60);

                // Format offset
                const sign = offsetMinutes >= 0 ? '+' : '-';
                const minutesPart =
                    minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';

                utcLabel = ` (UTC${sign}${hours}${minutesPart})`;
            }
        }

        return (
            <span>
                {formattedDate}
                {utcLabel}
            </span>
        );
    }

    DateValueComponent.displayName = 'DateValueComponent';

    return DateValueComponent;
}

const IMPACT_SCORE_CONFIG = {
    0: { count: 1, className: cls.impactScoreIconQuasiNull },
    1: { count: 2, className: cls.impactScoreIconLight },
    2: { count: 3, className: cls.impactScoreIconModerate },
    3: { count: 4, className: cls.impactScoreIconHigh },
} as const;

const IMPACT_SCORE_ICON_SIZE = 16;

const IMPACT_SCORE_LABELS = {
    0: 'newsCalendarImpactQuasiNull',
    1: 'newsCalendarImpactLight',
    2: 'newsCalendarImpactModerate',
    3: 'newsCalendarImpactHigh',
} as const;

interface ImpactScoreIconProps<T> {
    record: T;
    impactScoreSelector: (record: T) => number | undefined;
}

function ImpactScoreIcon<T>({
    record,
    impactScoreSelector,
}: ImpactScoreIconProps<T>): JSX.Element {
    const { t } = useTranslation();
    const impactScore = impactScoreSelector(record);
    const score = impactScore !== undefined ? impactScore : 0;
    const config =
        IMPACT_SCORE_CONFIG[score as keyof typeof IMPACT_SCORE_CONFIG] ||
        IMPACT_SCORE_CONFIG[0];
    const label =
        IMPACT_SCORE_LABELS[score as keyof typeof IMPACT_SCORE_LABELS] ||
        IMPACT_SCORE_LABELS[0];

    return (
        <Tooltip
            placement="topLeft"
            title={t(label)}
        >
            <div className={cls.impactScoreIcons}>
                {Array.from({ length: config.count }, (_unused, iconIndex) => (
                    <AlgotechLogo
                        key={`impact-icon-${score}-${iconIndex}`}
                        className={config.className}
                        height={IMPACT_SCORE_ICON_SIZE}
                        width={IMPACT_SCORE_ICON_SIZE}
                    />
                ))}
            </div>
        </Tooltip>
    );
}

export function createImpactScoreFormatter<T>(
    impactScoreSelector: (record: T) => number | undefined,
): (value: unknown, record: T) => JSX.Element {
    function ImpactScoreValueComponent(_: unknown, record: T): JSX.Element {
        return (
            <ImpactScoreIcon
                impactScoreSelector={impactScoreSelector}
                record={record}
            />
        );
    }

    ImpactScoreValueComponent.displayName = 'ImpactScoreValueComponent';

    return ImpactScoreValueComponent;
}
